import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ETableConfig } from '@shared/enums/ETableConfig';
import { ITableConfigInfo, ITableConfigInfoList, ITableConfigList, ITableConfigWithInfo } from '@shared/models/ITableConfig';
import { GroupApiService } from '@api/group-api/group-api.service';
import { TranslateService } from '@ngx-translate/core';
import { ITableConfig } from '@api/group-api/models';
import { IApiResponse } from '@shared/models/IApiResponse';
import { AFFILIATES_FIELDS } from '@shared/configurable/fields/affiliates.fields';
import { AGENTS_FIELDS } from '@shared/configurable/fields/agents.fields';
import { toTableDefaults, toTableSamples } from '@shared/configurable/field-registry';
import { CONTACTS_FIELDS } from '@shared/configurable/fields/contacts.fields';
import { INSURANCE_FIELDS } from '@shared/configurable/fields/insurance.fields';
import { OBJECT_FIELDS } from '@shared/configurable/fields/object.fields';
import { OBJECT_TARIFF_FIELDS } from '@shared/configurable/fields/object-tariff.fields';
import { INSURED_FIELDS } from '@shared/configurable/fields/insured.fields';
import { CLAIM_FIELDS } from '@shared/configurable/fields/claim.fields';
import { StorageService } from '@shared/service/storage.service';
import { IFieldDef } from '@shared/configurable/models/IFieldDef';

const TABLE_CONFIGS_STORAGE_KEY = 'tableConfigs';

/** Every configurable table paired with the field set that feeds it. */
const TABLE_FIELD_SOURCES: [ETableConfig, IFieldDef[]][] = [
  [ETableConfig.Insurance, INSURANCE_FIELDS],
  [ETableConfig.Claims, CLAIM_FIELDS],
  [ETableConfig.ClientClaims, CLAIM_FIELDS],
  [ETableConfig.Object, OBJECT_FIELDS],
  [ETableConfig.ObjectTariff, OBJECT_TARIFF_FIELDS],
  [ETableConfig.Contacts, CONTACTS_FIELDS],
  [ETableConfig.CrmList, INSURED_FIELDS],
  [ETableConfig.Affiliates, AFFILIATES_FIELDS],
  [ETableConfig.Agents, AGENTS_FIELDS],
];

@Injectable({
  providedIn: 'root',
})
export class TableConfigService {
  private readonly _api = inject(GroupApiService);
  private readonly _t = inject(TranslateService);
  private readonly _storage = inject(StorageService);

  private _tableConfigsDefault: ITableConfigList = Object.fromEntries(
    TABLE_FIELD_SOURCES.map(([type, fields]) => [type, toTableDefaults(type, fields)])
  ) as ITableConfigList;
  private _tableConfigsData: ITableConfigInfoList = Object.fromEntries(
    TABLE_FIELD_SOURCES.map(([type, fields]) => [type, toTableSamples(type, fields)])
  ) as ITableConfigInfoList;
  private _tableConfigs = new BehaviorSubject<ITableConfigList>(this._storage.get<ITableConfigList>(TABLE_CONFIGS_STORAGE_KEY));
  tableConfigs$ = this._tableConfigs.asObservable();

  setTableConfigs(configs: ITableConfigList) {
    this._tableConfigs.next(configs);
  }

  updateConfig(tableConfigs: ITableConfig[], tableType: ETableConfig): Observable<IApiResponse<any>> {
    const next: ITableConfigList = { ...this._tableConfigs.getValue(), [tableType]: tableConfigs };
    this.setTableConfigs(next);
    this._storage.set(TABLE_CONFIGS_STORAGE_KEY, next);
    return this._api.updateSettings(next[tableType]);
  }

  getTableConfigs(tableType: ETableConfig): ITableConfigWithInfo[] {
    const tableConfigs = this._tableConfigs.getValue()?.[tableType] || [];
    if (!tableConfigs.length) {
      return [];
    }

    return tableConfigs.map(item => ({
      ...item,
      info: this.getTableConfigData(item.tableType, item.controlName),
    }));
  }

  getTableConfigsForTable(tableType: ETableConfig): ITableConfig[] {
    const tableConfigs = this._tableConfigs.getValue()?.[tableType] || [];

    if (!tableConfigs.length) {
      return [];
    }
    return tableConfigs.filter(
      col =>
        !!col.show &&
        (col.controlName === 'controls' || col.tableType === ETableConfig.Object || this._t.instant(col.controlName + '.caption') !== '')
    );
  }

  getTableConfigsDefault(tableType: ETableConfig): ITableConfigWithInfo[] {
    const tableConfigs = this._tableConfigsDefault[tableType];
    return tableConfigs.map(item => ({
      ...item,
      info: this.getTableConfigData(item.tableType, item.controlName),
    }));
  }

  getTableConfigData(tableType: ETableConfig, controlName: string): ITableConfigInfo {
    return this._tableConfigsData[tableType][controlName];
  }

  fetchTableConfigs() {
    this._api.getSettings().subscribe(res => {
      if (res.data) {
        this.sortTableConfigs(res.data);
      }
    });
  }

  initTableConfigsFromStorage() {
    const stored = this._storage.get<ITableConfigList>(TABLE_CONFIGS_STORAGE_KEY);
    if (stored) {
      this.setTableConfigs(stored);
    } else {
      this.fetchTableConfigs();
    }
  }

  private sortTableConfigs(tableConfigs: ITableConfig[]) {
    const res = Object.fromEntries(TABLE_FIELD_SOURCES.map(([type]) => [type, []])) as ITableConfigList;
    for (const item of tableConfigs) {
      res[item.tableType]?.push(item);
    }
    for (const list of Object.values(res)) {
      list.sort((a, b) => a.order - b.order);
    }
    this.setTableConfigs(res);
    this._storage.set(TABLE_CONFIGS_STORAGE_KEY, res);
  }
}
